'use client';
import React, { useState, useRef, useEffect } from 'react';
import { Bell, X, CheckCheck, MessageCircle, Tag, CreditCard, CheckCircle, AlertCircle, ChevronRight } from 'lucide-react';
import { useNotifications, AppNotification } from '@/contexts/NotificationContext';
import Link from 'next/link';
import { useRouter } from 'next/navigation';

function getNotifIcon(type: string | null) {
  switch (type) {
    case 'message': return <MessageCircle size={14} className="text-[#3B82F6]" />;
    case 'offer': return <Tag size={14} className="text-[#F59E0B]" />;
    case 'payment': return <CreditCard size={14} className="text-[#10B981]" />;
    case 'approval': return <CheckCircle size={14} className="text-[#10B981]" />;
    case 'rejection': return <AlertCircle size={14} className="text-[#EF4444]" />;
    default: return <Bell size={14} className="text-[#A1A1A6]" />;
  }
}

function getNotifBg(type: string | null) {
  switch (type) {
    case 'message': return 'bg-blue-50';
    case 'offer': return 'bg-amber-50';
    case 'payment': return 'bg-emerald-50';
    case 'approval': return 'bg-emerald-50';
    case 'rejection': return 'bg-red-50';
    default: return 'bg-[#F5F5F7]';
  }
}

function formatTime(ts: string) {
  const d = new Date(ts);
  const now = new Date();
  const diff = now.getTime() - d.getTime();
  if (diff < 60000) return 'Just now';
  if (diff < 3600000) return `${Math.floor(diff / 60000)}m ago`;
  if (diff < 86400000) return `${Math.floor(diff / 3600000)}h ago`;
  return d.toLocaleDateString('en-IN', { day: 'numeric', month: 'short' });
}

export default function NotificationCenter() {
  const { notifications, unreadCount, markAsRead, markAllAsRead } = useNotifications();
  const [open, setOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const router = useRouter();

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleNotifClick = async (notif: AppNotification) => {
    if (!notif.is_read) await markAsRead(notif.id);
    if (notif.action_url) {
      router.push(notif.action_url);
      setOpen(false);
    }
  };

  return (
    <div className="relative" ref={dropdownRef}>
      <button
        onClick={() => setOpen(!open)}
        className="relative w-9 h-9 rounded-[10px] border border-[#E2E2E7] flex items-center justify-center text-[#6E6E73] hover:text-[#D93025] hover:border-[#D93025] transition-all duration-150"
        aria-label="Notifications"
      >
        <Bell size={15} />
        {unreadCount > 0 && (
          <span className="absolute -top-1.5 -right-1.5 min-w-[18px] h-[18px] bg-[#D93025] text-white text-[10px] font-700 rounded-full flex items-center justify-center px-1">
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute right-0 top-full mt-2 w-[360px] bg-white border border-[#E2E2E7] rounded-[16px] shadow-modal z-50 overflow-hidden animate-fade-in">
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-[#F0F0F5]">
            <div className="flex items-center gap-2">
              <h3 className="font-700 text-[#1D1D1F] text-[14px]">Notifications</h3>
              {unreadCount > 0 && (
                <span className="bg-[#FFEBEE] text-[#D93025] text-[11px] font-700 px-2 py-0.5 rounded-full">{unreadCount} new</span>
              )}
            </div>
            <div className="flex items-center gap-2">
              {unreadCount > 0 && (
                <button
                  onClick={markAllAsRead}
                  className="flex items-center gap-1 text-[12px] text-[#D93025] hover:text-[#B71C1C] font-500 transition-colors"
                >
                  <CheckCheck size={13} />
                  Mark all read
                </button>
              )}
              <button onClick={() => setOpen(false)} className="w-6 h-6 rounded-full flex items-center justify-center text-[#A1A1A6] hover:bg-[#F5F5F7] transition-colors">
                <X size={13} />
              </button>
            </div>
          </div>

          {/* Notifications List */}
          <div className="overflow-y-auto" style={{ maxHeight: '380px' }}>
            {notifications.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-10 px-4 text-center">
                <div className="w-12 h-12 rounded-full bg-[#F5F5F7] flex items-center justify-center mb-3">
                  <Bell size={20} className="text-[#A1A1A6]" />
                </div>
                <p className="font-600 text-[#1D1D1F] text-[13px] mb-1">No notifications yet</p>
                <p className="text-[12px] text-[#A1A1A6]">You'll see updates about your listings and offers here</p>
              </div>
            ) : (
              notifications.map(notif => (
                <button
                  key={notif.id}
                  onClick={() => handleNotifClick(notif)}
                  className={`w-full flex items-start gap-3 px-4 py-3 text-left border-b border-[#F0F0F5] last:border-0 transition-colors ${notif.is_read ? 'hover:bg-[#F5F5F7]' : 'bg-[#FFFBF0] hover:bg-[#FFF8E1]'}`}
                >
                  <div className={`w-8 h-8 rounded-full ${getNotifBg(notif.notification_type)} flex items-center justify-center flex-shrink-0 mt-0.5`}>
                    {getNotifIcon(notif.notification_type)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-2">
                      <p className={`text-[13px] leading-snug ${notif.is_read ? 'font-500 text-[#3D3D3F]' : 'font-600 text-[#1D1D1F]'}`}>
                        {notif.title}
                      </p>
                      {!notif.is_read && <span className="w-2 h-2 rounded-full bg-[#D93025] flex-shrink-0 mt-1" />}
                    </div>
                    {notif.body && <p className="text-[12px] text-[#6E6E73] mt-0.5 line-clamp-2">{notif.body}</p>}
                    <p className="text-[11px] text-[#A1A1A6] mt-1">{formatTime(notif.created_at)}</p>
                  </div>
                  {notif.action_url && <ChevronRight size={13} className="text-[#C7C7CC] flex-shrink-0 mt-1" />}
                </button>
              ))
            )}
          </div>

          {/* Footer */}
          {notifications.length > 0 && (
            <div className="px-4 py-3 border-t border-[#F0F0F5]">
              <Link
                href="/notifications"
                onClick={() => setOpen(false)}
                className="block text-center text-[13px] font-600 text-[#D93025] hover:text-[#B71C1C] transition-colors"
              >
                View all notifications
              </Link>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
